import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  MemberHealthDistribution,
  OverviewCard,
  PageTitleAndSubTitle,
  DashedStandardDropdown,
  CostTrendChart,
  Breadcrumbs,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import {
  CostValues,
  formatDiseaseCostValues,
  formatDiseaseTotalCost,
  formatMemberValues,
  MemberValues,
  TotalCost,
} from '../../core-utils/helper-utils/summaryReportHelper';
import properties from '../../core-utils/properties';
import Loader from '../../_components/molecules/Loader';
import chronicPrevalencePageConstants from '../../core-utils/messages/chronicPrevalencePageConstants';
import { useNavigate } from 'react-router-dom';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { useStores } from '../../stores';
import DiseasePrevalenceTable from '../../_components/organisms/DiseasePrevalenceTable';
import ReportsModal from '../../_components/organisms/ReportsModal';
import { ENROLLMENT_METRICS, PLAN_PERFORMANCE } from '../../core-utils/routes';
import TabSelection from '../../_components/molecules/TabSelection';
import RecommendationsTab from '../../_components/organisms/RecommendationsTab';
import { getRecommendationsData } from '../../core-utils/helper-utils/recommendationsTabHelper';
import { getCohortValue } from '../../core-utils/helper-utils/overviewCardHelper';
import OverviewCardTooltipContent from '../../_components/atoms/OverviewCardTooltipContent';
import {
  addSortedPrevalence,
  AgeGroupRow,
  buildPrevalenceByAgeGroupGraphData,
  buildPrevalenceByGenderGraphData,
  CostRow,
  GenderRow,
  mergeByKey,
} from '../../core-utils/helper-utils/diseasePrevlanceHelper';
import useTotalEligible from '../../core-utils/custom-hooks/TotalEligible';
import usePlanDates from '../../core-utils/custom-hooks/PlanDates';
import { useAPIPayload } from '../../core-utils/helper-utils/apiPayloadHelper';
import DateFilter from '../../_components/molecules/DateFilter';
import { observer } from 'mobx-react';
import { downloadPdf } from '../../core-utils/helper-utils/downloadPdfHelper';
import Snackbar from '../../_components/atoms/Snackbar';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import componentConstants from '../../core-utils/messages/componentConstants';
import AiSummaryCard from '../../_components/molecules/AiSummaryCard';
import allScreenLabel from '../../core-utils/messages/screenNamesForPrompts';
import {
  checkUserAccess,
  UserAccessConstants,
} from '../../core-utils/helper-utils/userAccess';
import CohortDetailsModal from '../../_components/organisms/CohortDetailsModalNew';
import FeatureAccessModal from '../../_components/organisms/FeatureAccessModal';
import { useCohortModal } from '../../core-utils/custom-hooks/useCohortModal';
import { useCohortOnPageLoad } from '../../core-utils/custom-hooks/useCohortPageOnLoad';
import CohortFilterBar from '../../_components/molecules/CohortFilterBar';

export interface ChronicPrevalenceCondition {
  label: string;
  value: string;
  svalue: string;
}

export interface ChronicPrevalenceConditionAPIResponse {
  data: any;
  status: any;
}

export interface ChronicPrevalenceConditionMain {
  label: string;
  svalue: string;
  data: ChronicPrevalenceCondition[];
}

export interface MemberHealthConditionProps {
  totalMembers: number | string;
  mentalCondition: number | string;
  physicalCondition: number | string;
  both: number | string;
  neither: number | string;
}

export interface PrevalenceByGenderProps {
  totalMembers: number;
  mentalCondition: number;
  physicalCondition: number;
  both: number;
  neither: number;
}

export interface SummaryPageProps {}

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: '40px 0px 66px 63px',
  },
});

export const getChronicSubTitle = () => {
  return (
    <Typography
      fontSize={'14px'}
      lineHeight="177%"
      textColor={patientAccessTheme.palette.text.mediumemphasis}
      fontType="regular"
    >
      Chronic conditions are identified using the{' '}
      <a
        href="https://drive.google.com/drive/folders/1KxU9pFL2MER7HUiJQcpYYuEHo6MA59Z1?usp=drive_link"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: patientAccessTheme.palette.primary.main,
          textDecoration: 'underline',
        }}
      >
        CMS chronic condition algorithms
      </a>{' '}
      (last updated Mar 2025) — costs shown reflect medical claims only.
    </Typography>
  );
};

const ChronicPrevalencePage: React.FC<SummaryPageProps> = observer(({}) => {
  const { classes } = useStyles();
  const employerGroupStore = useStores().employerGroupStore;
  const snackBarMessages = componentConstants.ORGANISMS.REPORTS_MODAL;
  const messages = chronicPrevalencePageConstants.CHRONIC_PREVALENCE_PAGE;
  const loginPageStore = useStores().loginPageStore;
  const cohortDetailsStore = useStores().cohortDetailsStore;
  const chronicPrevalenceStore = useStores().chronicPrevalenceStore;
  const quaterlyReportStore = useStores().quaterlyReportStore;
  const {
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    isIncurredDate,
    isPaidDate,
    isPlanDatesFetched,
    aggregationParameter,
  } = quaterlyReportStore;

  const { getPlanDates, loading: isPlanDatesLoading } = usePlanDates();
  const payload = useAPIPayload();

  const {
    overAllEnrolled,
    overallTotalCost,
    getTotalEligible,
  } = useTotalEligible(
    aggregationParameter === 'paid' ? paidStartDate : incurredStartDate,
    aggregationParameter === 'paid' ? paidEndDate : incurredEndDate,
    employerGroupStore,
  );

  const [openFeatureAccessModal, setOpenFeatureAccessModal] = useState(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [totalCost, setTotalCost] = useState<TotalCost>({
    totalCost: '$0.00',
    totalRx: '$0.00',
    totalMedical: '$0.00',
  });
  const [costValues, setCostValues] = useState<CostValues>({
    rx: '$0.00',
    medical: '$0.00',
    total: '$0.00',
  });
  const [memberValues, setMemberValues] = useState<MemberValues>({
    members: '0',
    employees: '0',
    dependents: '0',
  });
  const [conditionDropdownValue, setConditionDropdownValue] = useState(
    'Gender',
  );
  const screenName = allScreenLabel;
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [activeAskAi, setActiveAskAi] = useState<boolean>(false);
  const [aiSummaryData, setAiSummaryData] = useState<
    { title: string; description: string }[]
  >([]);

  // required for later
  // const [pmpm, setPMPM] = useState<PMPM>({
  //   pmpm: '',

  //   pmpmPercentage: '',
  //   increment: true,
  // });

  // required for later
  // const [pepm, setPEPM] = useState<PEPM>({
  //   pepm: '',
  //   pepmPercentage: '',
  //   increment: true,
  // });

  const [
    memberHealthData,
    setMemberHealthData,
  ] = useState<MemberHealthConditionProps>();

  const [
    prevalenceByGenderConditionGraphDataSet,
    setPrevalenceByGenderConditionGraphDataSet,
  ] = useState<any>([
    {
      seriesname: 'Male members',
      plottooltext: 'Male members - $dataValue',
      color: patientAccessTheme.palette.primary[300],
      data: [],
    },
    {
      seriesname: 'Female members',
      plottooltext: 'Female members - $dataValue',
      color: patientAccessTheme.palette.primary[200],
      data: [],
    },
    {
      seriesname: 'Cost',
      plottooltext: 'cost- $$dataValue',
      data: [],
      color: patientAccessTheme.palette.warning[500],
      parentyaxis: 'S',
      renderas: 'line',
      anchorBgColor: patientAccessTheme.palette.warning[500],
    },
  ]);

  const [
    prevalenceByAgeGroupConditionGraphDataSet,
    setPrevalenceByAgeGroupConditionGraphDataSet,
  ] = useState<any>([
    {
      seriesName: '0-18',
      plottooltext: '0-18 : $dataValue',
      color: patientAccessTheme.palette.primary[200],
      data: [],
    },
    {
      seriesName: '19-26',
      plottooltext: '19-26 : $dataValue',
      color: patientAccessTheme.palette.primary[300],
      data: [],
    },
    {
      seriesName: '27-40',
      plottooltext: '27-40 : $dataValue',
      color: patientAccessTheme.palette.primary[400],
      data: [],
    },
    {
      seriesName: '41-64',
      plottooltext: '41-64 : $dataValue',
      color: patientAccessTheme.palette.primary[500],
      data: [],
    },
    {
      seriesName: '65+',
      plottooltext: '65+ : $dataValue',
      color: patientAccessTheme.palette.primary[600],
      data: [],
    },
    {
      seriesname: 'Cost',
      plottooltext: 'cost- $$dataValue',
      data: [],
      color: patientAccessTheme.palette.warning[500],
      parentyaxis: 'S',
      renderas: 'line',
      anchorBgColor: patientAccessTheme.palette.warning[500],
    },
  ]);

  const [prevalenceGraphData, setPrevalenceGraphData] = useState<any>(
    prevalenceByGenderConditionGraphDataSet,
  );
  const [isConditionsLoading, setIsConditionsLoading] = useState<boolean>(
    false,
  );

  const [
    chronicPrevalenceTableData,
    setChronicPrevalenceTableData,
  ] = useState<any>({});
  const [openReportsModal, setOpenReportsModal] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [recommendationLoading, setRecommendationLoading] = useState<boolean>(
    false,
  );
  const [recommendationFetched, setRecommendationFetched] = useState<boolean>(
    false,
  );
  const { open, openModal, closeModal, isEditMode } = useCohortModal();
  const { totalResults, memberIds, isInitializing } = useCohortOnPageLoad({
    fetchTotalResults: () => getChronicCohortMembers(),
    enabled:
      isPlanDatesFetched &&
      !!employerGroupStore?.employerGroupResponseData?.eg_nid?.[0],
    isInternal: true,
  });

  //snackbar
  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });

  const handleSelectedIndex = (index: number) => {
    setSelectedTabIndex(index);
    if (!recommendationFetched) {
      getRecommendationsData(
        employerGroupStore?.employerGroupResponseData?.eg_nid,
        messages.TITLE,
        recommendedTabStore,
        setRecommendationLoading,
      );
      setRecommendationFetched(true);
    }
  };

  const handleDiseaseDropDownList = (chronicPrevalenceTableData: any) => {
    const localDiseasesDropDownList: string[] = [];
    const localDiseasesMembersMap: Map<string, any> = new Map<string, any>();
    chronicPrevalenceTableData?.map((diseaseSummaryObject: any) => {
      diseaseSummaryObject?.individualDiseases?.map(
        (individualDiseaseSummary: any) => {
          localDiseasesDropDownList.push(
            individualDiseaseSummary?.chronicDiseaseName,
          );
          localDiseasesMembersMap.set(
            individualDiseaseSummary?.chronicDiseaseName,
            {
              memberList: individualDiseaseSummary?.disease_member_list,
              medicalCost: individualDiseaseSummary?.medicalCost,
              rxCost: individualDiseaseSummary?.rxCost,
              pmpm: individualDiseaseSummary?.pmpm,
            },
          );
        },
      );
    });
    chronicPrevalenceStore.setDiseasesDropDownList(localDiseasesDropDownList);
    chronicPrevalenceStore.setDiseasesMembersMap(localDiseasesMembersMap);
  };

  const handleAskAi = () => {
    setActiveAskAi(true);
    getAiSummarizedData();
  };

  const getTotalCost = async (member_unique_id_list: string[]) => {
    await axios
      .post(properties.aiqBaseUrl + properties.summaryBaseUrl, {
        ...payload,
        claim_id_list: [],
        member_unique_id_list: member_unique_id_list,
        max_claim_date:
          employerGroupStore?.employerGroupResponseData?.max_claim_date,
      })
      .then((res) => {
        if (res?.status === 200) {
          setMemberValues(formatMemberValues(res?.data?.data));
        }
      })
      .catch((ex) => {
        return ex;
      });
  };

  // const getPMPM = async (member_unique_id_list: string[]) => {
  //   await axios
  //     .post(
  //       properties.aiqBaseUrl + properties.summaryBaseUrl + properties.pmpm,
  //       {
  //         startDate: startDate,
  //         endDate: endDate,
  //         eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
  //         claim_id_list: [],
  //         member_unique_id_list: member_unique_id_list,
  //       },
  //     )
  //     .then((res) => {
  //       if (res?.status === 200) {
  //         setPMPM(formatPMPM(res?.data?.data));
  //       }
  //     })
  //     .catch((ex) => {
  //       return ex;
  //     });
  // };

  // const getPEPM = async (member_unique_id_list: string[]) => {
  //   await axios
  //     .post(
  //       properties.aiqBaseUrl + properties.summaryBaseUrl + properties.pepm,
  //       {
  //         startDate: startDate,
  //         endDate: endDate,
  //         eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
  //         claim_id_list: [],
  //         member_unique_id_list: member_unique_id_list,
  //       },
  //     )
  //     .then((res) => {
  //       if (res?.status === 200) {
  //         setPEPM(formatPEPM(res?.data?.data));
  //       }
  //     })
  //     .catch((ex) => {
  //       return ex;
  //     });
  // };

  const getChronicPrevalenceTableData = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.chronicPrevalence +
          properties.prevalenceSummary,
        {
          ...payload,
          claim_id_list: [],
          member_unique_id_list: cohortDetailsStore.memberUniqueIdList || [],
          max_claim_date:
            employerGroupStore?.employerGroupResponseData?.max_claim_date,
        },
      )
      .then(async (res) => {
        if (res?.status === 200) {
          setChronicPrevalenceTableData(addSortedPrevalence(res?.data?.data));
          handleDiseaseDropDownList(res?.data?.data?.prevalenceSummary);
          await Promise.all([
            setTotalCost(
              formatDiseaseTotalCost(res?.data?.data?.prevalenceSummary, 2),
            ),
            setCostValues(
              formatDiseaseCostValues(res?.data?.data?.prevalenceSummary),
            ),
            Promise.all([
              getTotalCost(res?.data?.data?.total_members_list),
              getpolyChronicPrevalenceGraphData(
                res?.data?.data?.total_members_list,
              ),
            ]).then(() => {
              setLoading(false);
            }),
          ]);
        }
      })
      .catch((ex) => {
        return ex;
      });
  };

  const getChronicCohortMembers = async (): Promise<{
    noOfMemberIds: number;
    member_unique_ids: string[];
  }> => {
    try {
      const res = await axios.post(
        properties.aiqBaseUrl +
          properties.chronicPrevalence +
          properties.getChronicCohortMembers,
        {
          ...payload,
          claim_id_list: [],
          member_unique_id_list: cohortDetailsStore.memberUniqueIdList || [],
          max_claim_date:
            employerGroupStore?.employerGroupResponseData?.max_claim_date,
        },
      );

      if (res?.status === 200) {
        const data = res?.data?.data;
        return {
          noOfMemberIds: data?.noOfMemberIds ?? 0,
          member_unique_ids: data?.member_unique_ids ?? [],
        };
      }

      // ❗ non-200 response
      setSnackbarState({
        open: true,
        message: 'Failed to fetch chronic cohort members',
        severity: 'error',
        duration: 3000,
      });

      return { noOfMemberIds: 0, member_unique_ids: [] };
    } catch (error: any) {
      setSnackbarState({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Something went wrong',
        severity: 'error',
        duration: 4000,
      });
      return { noOfMemberIds: 0, member_unique_ids: [] };
    } finally {
      setLoading(false);
    }
  };

  const recommendedTabStore = useStores().recommendedTabStore;
  const navigate = useNavigate();

  const handleDiseaseCategory = (
    diseaseCategory: string,
    memberList: string[],
    isDiseaseGroup: boolean,
  ) => {
    recommendedTabStore?.setCategoryToBeCalled(
      messages.PREVALENCE_BY + ' ' + diseaseCategory,
    );
    navigate(`/chronic-prelavence/${encodeURIComponent(diseaseCategory)}`, {
      state: {
        memberList,
        isDiseaseGroup,
      },
    });
  };

  const getpolyChronicPrevalenceGraphData = async (
    member_unique_id_list: string[],
  ) => {
    const polyChronicPrevalenceTotalCostData: { cost: string }[] = [
      {
        cost: '24',
      },
      {
        cost: '24',
      },
      {
        cost: '24',
      },
      {
        cost: '24',
      },
      {
        cost: '24',
      },
    ];

    await axios
      .post(
        properties.aiqBaseUrl +
          properties.chronicPrevalence +
          properties.polyDiseasePrev,
        {
          ...payload,
          member_unique_id_list,
        },
      )
      .then((response) => {
        const polyChronicPrevalenceTotalCostData = response.data
          .data as CostRow[];

        getPrevalenceByGenderCondition(
          polyChronicPrevalenceTotalCostData,
          member_unique_id_list,
        );
        getPrevalenceByAgeGroupCondition(
          polyChronicPrevalenceTotalCostData,
          member_unique_id_list,
        );
      })
      .catch((ex) => {
        return ex;
      });
    return polyChronicPrevalenceTotalCostData;
  };

  const getMemberHealthDistribution = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.chronicPrevalence +
          properties.healthDistribution,
        {
          ...payload,
          claim_id_list: [],
          member_unique_id_list: cohortDetailsStore.memberUniqueIdList,
          max_claim_date:
            employerGroupStore?.employerGroupResponseData?.max_claim_date,
        },
      )
      .then((response) => {
        const healthData: MemberHealthConditionProps = {
          totalMembers: '0',
          physicalCondition: '0',
          mentalCondition: '0',
          both: '0',
          neither: '0',
        };
        const responseHealthData = response?.data?.data;
        healthData.physicalCondition =
          responseHealthData?.count_of_phyical_condition_members &&
          responseHealthData?.count_of_phyical_condition_members !== 0
            ? responseHealthData?.count_of_phyical_condition_members
            : '0';

        healthData.mentalCondition =
          responseHealthData?.count_of_mental_condition_members &&
          responseHealthData?.count_of_mental_condition_members !== 0
            ? responseHealthData?.count_of_mental_condition_members
            : '0';
        healthData.both =
          responseHealthData?.count_of_both_condition_members &&
          responseHealthData?.count_of_both_condition_members !== 0
            ? responseHealthData?.count_of_both_condition_members
            : '0';
        healthData.neither =
          responseHealthData?.count_of_neither_condition_members &&
          responseHealthData?.count_of_neither_condition_members !== 0
            ? responseHealthData?.count_of_neither_condition_members
            : '0';
        healthData.totalMembers =
          Number(healthData.physicalCondition) +
          Number(healthData.mentalCondition) +
          Number(healthData.both);
        healthData.totalMembers =
          healthData.totalMembers && healthData.totalMembers !== 0
            ? healthData.totalMembers
            : '0';
        setMemberHealthData(healthData);
      })
      .catch((ex) => {
        return ex;
      });
  };

  const getPrevalenceByGenderCondition = async (
    totalCostData: CostRow[],
    member_unique_id_list: string[],
  ) => {
    setIsConditionsLoading(true);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.chronicPrevalence +
          properties.prevalenceByGender,
        {
          ...payload,
          member_unique_id_list: member_unique_id_list,
        },
      )
      .then((response) => {
        const genderData = response.data.data as GenderRow[];

        const merged = mergeByKey(
          genderData,
          totalCostData,
          'no_of_conditions',
          'total_cost',
        );

        const {
          maleData,
          femaleData,
          costData,
        } = buildPrevalenceByGenderGraphData(merged);

        setPrevalenceByGenderConditionGraphDataSet([
          {
            seriesname: 'Male members',
            plottooltext: 'Male members - $dataValue',
            color: patientAccessTheme.palette.primary[300],
            data: maleData,
          },
          {
            seriesname: 'Female members',
            plottooltext: 'Female members - $dataValue',
            color: patientAccessTheme.palette.primary[200],
            data: femaleData,
          },
          {
            seriesname: 'Cost',
            plottooltext: 'Cost - $$dataValue',
            data: costData,
            color: patientAccessTheme.palette.warning[500],
            parentyaxis: 'S',
            renderas: 'line',
            anchorBgColor: patientAccessTheme.palette.warning[500],
          },
        ]);
        setPrevalenceGraphData([
          {
            seriesname: 'Male members',
            plottooltext: 'Male members - $dataValue',
            color: patientAccessTheme.palette.primary[300],
            data: maleData,
          },
          {
            seriesname: 'Female members',
            plottooltext: 'Female members - $dataValue',
            color: patientAccessTheme.palette.primary[200],
            data: femaleData,
          },
          {
            seriesname: 'Cost',
            plottooltext: 'Cost - $$dataValue',
            data: costData,
            color: patientAccessTheme.palette.warning[500],
            parentyaxis: 'S',
            renderas: 'line',
            anchorBgColor: patientAccessTheme.palette.warning[500],
          },
        ]);
      })
      .catch((ex) => {
        return ex;
      });
    setIsConditionsLoading(false);
  };

  const getPrevalenceByAgeGroupCondition = async (
    totalCostData: CostRow[],
    member_unique_id_list: string[],
  ) => {
    setIsConditionsLoading(true);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.chronicPrevalence +
          properties.prevalenceByAge,
        {
          ...payload,
          member_unique_id_list: member_unique_id_list,
        },
      )
      .then((response) => {
        const ageData = response.data.data as AgeGroupRow[];
        const merged = mergeByKey(
          ageData,
          totalCostData,
          'no_of_conditions',
          'total_cost',
        );

        const {
          age0to18Data,
          age19to26Data,
          age27to40Data,
          age41to64Data,
          age65PlusData,
          costData,
        } = buildPrevalenceByAgeGroupGraphData(merged);

        setPrevalenceByAgeGroupConditionGraphDataSet([
          {
            seriesName: '0-18',
            plottooltext: '0-18 : $dataValue',
            color: patientAccessTheme.palette.primary[200],
            data: age0to18Data,
          },
          {
            seriesName: '19-26',
            plottooltext: '19-26 : $dataValue',
            color: patientAccessTheme.palette.primary[300],
            data: age19to26Data,
          },
          {
            seriesName: '27-40',
            plottooltext: '27-40 : $dataValue',
            color: patientAccessTheme.palette.primary[400],
            data: age27to40Data,
          },
          {
            seriesName: '41-64',
            plottooltext: '41-64 : $dataValue',
            color: patientAccessTheme.palette.primary[500],
            data: age41to64Data,
          },
          {
            seriesName: '65+',
            plottooltext: '65+ : $dataValue',
            color: patientAccessTheme.palette.primary[600],
            data: age65PlusData,
          },
          {
            seriesname: 'Cost',
            plottooltext: 'cost- $$dataValue',
            data: costData,
            color: patientAccessTheme.palette.warning[500],
            parentyaxis: 'S',
            renderas: 'line',
            anchorBgColor: patientAccessTheme.palette.warning[500],
          },
        ]);
      })
      .catch((ex) => {
        return ex;
      });
    setIsConditionsLoading(false);
  };

  const getAllCharts = async () => {
    setLoading(true);
    isPlanDatesFetched &&
      (await Promise.all([
        getMemberHealthDistribution(),
        getChronicPrevalenceTableData(),
        getTotalEligible(),
      ]).then(() => {
        setLoading(false);
      }));
  };

  const getAiSummarizedData = async () => {
    try {
      setAiLoading(true);

      const cleanChronicData = {
        highestPrevalenceRate:
          chronicPrevalenceTableData?.highestPrevalenceRate,
        prevalenceSummary: chronicPrevalenceTableData?.prevalenceSummary?.map(
          (item: any) => ({
            chronicDiseaseGroupName: item?.chronicDiseaseGroupName,
            totalCost: item?.totalCost,
            memberCount: item?.memberCount,
            prevalenceRate: item?.prevalenceRate,
            pmpm: item?.pmpm,
            averageCost: item?.averageCost,
            highCost: item?.highCost,
            medicalPercent: item?.medicalPercent,
            rxPercent: item?.rxPercent,
            individualDiseases: item?.individualDiseases?.map((d: any) => ({
              chronicDiseaseName: d?.chronicDiseaseName,
              totalCost: d?.totalCost,
              memberCount: d?.memberCount,
              prevalenceRate: d?.prevalenceRate,
              pmpm: d?.pmpm,
              averageCost: d?.averageCost,
              highCost: d?.highCost,
              medicalPercent: d?.medicalPercent,
              rxPercent: d?.rxPercent,
            })),
          }),
        ),
      };

      const cleanGraphData = prevalenceGraphData?.map((series: any) => ({
        seriesname: series?.seriesname,
        data: series?.data,
      }));

      const apiResponses = {
        title_1: memberHealthData,
        title_2: cleanChronicData,
        title_3: cleanGraphData,
      };

      let resultArray = [];

      try {
        const response = await axios.post(
          properties.aiqBaseUrl +
            properties.contractBaseUrl +
            properties.getAiSummary,
          {
            screenName: screenName.CHRONIC_PREVALENCE,
            eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid?.[0],
            incurredStartDate,
            incurredEndDate,
            paidStartDate,
            paidEndDate,
            apiData: apiResponses,
          },
        );

        // ✅ Safe extraction
        resultArray = response?.data?.data?.data?.result || [];
        setAiSummaryData(Array.isArray(resultArray) ? resultArray : []);
      } catch (apiError) {
         
        console.error('AI Summary API failed:', apiError);
        // ✅ Optional: show user-friendly message
        handleOpenSnackBar('Failed to load summary', 'warning', 4000);
        setAiSummaryData([]);
      }
    } catch (error) {
       
      console.error('Unexpected Error:', error);
      handleOpenSnackBar(
        'Something went wrong. Please try again.',
        'error',
        6000,
      );
      setAiSummaryData([]);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (isInitializing) {
      return;
    }
    getAllCharts();
  }, [
    isInitializing,
    isPlanDatesFetched,
    isIncurredDate,
    isPaidDate,
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
    cohortDetailsStore.conditionsDetails,
    cohortDetailsStore.memberUniqueIdList,
  ]);
  useEffect(() => {
    const fetchPlanDates = async () => {
      if (!isPlanDatesFetched) {
        await getPlanDates();
      }
    };
    if (!employerGroupStore?.employerGroupResponseData?.eg_nid[0]) {
      return;
    }
    fetchPlanDates();
  }, [employerGroupStore?.employerGroupResponseData?.eg_nid]);

  useEffect(() => {
    setIsConditionsLoading(true);

    if (conditionDropdownValue === 'Gender') {
      setPrevalenceGraphData(prevalenceByGenderConditionGraphDataSet);
    } else {
      setPrevalenceGraphData(prevalenceByAgeGroupConditionGraphDataSet);
    }
    setIsConditionsLoading(false);
  }, [conditionDropdownValue]);

  //click Here Navigation Functions -
  const pushToEnrollemntMetrics = () => {
    navigate(ENROLLMENT_METRICS);
  };
  const pushToPlanPerformance = () => {
    navigate(PLAN_PERFORMANCE);
  };

  const SummaryCards = () => {
    const useStyles = makeStyles()({
      root: {
        display: 'flex',
        flexDirection: 'column',
      },
    });

    const { classes } = useStyles();

    return (
      <Grid container className={classes.root}>
        <Grid style={{ width: '1182px' }}>
          <CohortFilterBar />
        </Grid>
        {false && (
          <Grid style={{ display: 'flex', marginBottom: '29px' }}>
            <Grid style={{ marginRight: '24px' }}>
              <OverviewCardTooltipContent
                type={'member'}
                cohortMemberValues={memberValues}
                totalMemberValues={{
                  dependents: overAllEnrolled.dependents,
                  employees: overAllEnrolled.employees,
                  members: overAllEnrolled.totalCount,
                }}
                overviewCard={
                  <OverviewCard
                    title={messages.SUMMARY_CARDS.MEMBERS}
                    value={getCohortValue(
                      memberValues.members,
                      overAllEnrolled.totalCount,
                    )}
                    rootStyles={{
                      width: '176px',
                      fontWeight: 600,
                      fontSize: ' 24px',
                    }}
                    showClickHere
                    handleClickHere={pushToEnrollemntMetrics}
                  />
                }
              />
            </Grid>
            <Grid style={{ marginRight: '24px' }}>
              <OverviewCardTooltipContent
                type={'cost'}
                cohortCostValues={costValues}
                totalCostValues={{
                  medical: overallTotalCost.totalMedical,
                  rx: overallTotalCost.totalRx,
                  total: overallTotalCost.totalCost,
                }}
                overviewCard={
                  <OverviewCard
                    title={messages.SUMMARY_CARDS.TOTAL_COST}
                    value={getCohortValue(
                      (totalCost?.totalCost).toString(),
                      overallTotalCost.totalCost,
                    )}
                    rootStyles={{
                      width: '216px',
                    }}
                    labelsMinWidth="72px"
                    showClickHere
                    handleClickHere={pushToPlanPerformance}
                  />
                }
              />
            </Grid>
            {/* <Grid style={{ marginRight: '24px' }}>
            <OverviewCard
              title={messages.SUMMARY_CARDS.PMPM}
              topLabelValues={getPmpmForCohorts(
                pmpm,
                [],
                [],
              )}
              value={getPmpmValueForCohorts(
                pmpm,
                [],
                [],
              )}
              rootStyles={{
                width: '216px',
              }}
              showClickHere
              handleClickHere={pushToPlanPerformance}
            />
          </Grid> */}
            {/* <Grid style={{ marginRight: '24px' }}>
            <OverviewCard
              title={messages.SUMMARY_CARDS.PEPM}
              topLabelValues={getPepmForCohorts(
                pepm,
                [],
                [],
              )}
              value={getPepmValueForCohorts(
                pepm,
                [],
                [],
              )}
              rootStyles={{
                width: '216px',
              }}
              showClickHere
              handleClickHere={pushToPlanPerformance}
            />
          </Grid> */}
          </Grid>
        )}
        <Grid style={{ display: 'flex', marginBottom: '28px' }}>
          <Grid style={{ marginRight: '10px' }}>
            <MemberHealthDistribution
              title={messages.MEMBER_HEALTH_DISTRIBUTION.TITLE}
              subTitle={messages.MEMBER_HEALTH_DISTRIBUTION.SUB_TITLE}
              width="577px"
              height="386px"
              data={[
                {
                  id: 'main',
                  label: 'Total Population ',
                  value: '',
                  color: '#FFFFFF',
                },

                {
                  id: 'Diagnosed Population',
                  label: 'Chronic Population ',
                  parent: 'main',
                  value: memberHealthData?.totalMembers,
                  color: '#F66F30',
                },
                {
                  id: 'Undiagnosed Population',
                  label: 'Non Chronic Population ',
                  parent: 'main',
                  value: memberHealthData?.neither,
                  color: '#6C7DC0',
                },
                {
                  id: 'Only Mental Condition',
                  label: 'Only Mental Condition ',
                  parent: 'Diagnosed Population',
                  value: memberHealthData?.mentalCondition,
                  color: '#F88C5A',
                },
                {
                  id: 'Only Physical Condition',
                  label: 'Only Physical Condition ',
                  parent: 'Diagnosed Population',
                  value: memberHealthData?.physicalCondition,
                  color: '#F8A179',
                },
                {
                  id: 'Both Mental and Physical Condition',
                  label: 'Both Mental and Physical Condition ',
                  parent: 'Diagnosed Population',
                  value: memberHealthData?.both,
                  color: '#F57F48',
                },
              ]}
            />
          </Grid>
          <Grid style={{ marginLeft: '10px', display: 'flex' }}>
            <CostTrendChart
              chartTitle={
                <Grid container>
                  <Grid>
                    <Typography
                      textColor={patientAccessTheme.palette.text.highemphasis}
                      fontSize="14px"
                      fontType="semiBold"
                      lineHeight="24px"
                    >
                      {messages.PREVALENCE_BY_CONDITION_GRAPH.TITLE}
                    </Typography>
                  </Grid>
                  <Grid style={{ marginLeft: '5px' }}>
                    <DashedStandardDropdown
                      options={['Gender', 'Age']}
                      displayValue={['Gender', 'Age']}
                      value={conditionDropdownValue}
                      handleChange={(event: any) => {
                        const value: string = event.target.value as string;
                        setConditionDropdownValue(value);
                      }}
                    />
                  </Grid>
                </Grid>
              }
              chartSubTitle={
                conditionDropdownValue === 'Gender'
                  ? messages.PREVALENCE_BY_CONDITION_GRAPH.SUB_TITLE_GENDER
                  : messages.PREVALENCE_BY_CONDITION_GRAPH.SUB_TITLE_AGE_GROUP
              }
              cardHeight="386px"
              cardWidth="576px"
              chartHeight="270"
              chartWidth="570"
              xAxisValues={messages.PREVALENCE_BY_CONDITION_GRAPH.CATEGORIES}
              xAxisTitle="Conditions"
              yAxisTitle="Members"
              sAxisTitle="Cost$"
              datasetProp={prevalenceGraphData}
              isLoading={isConditionsLoading}
              // tooltip={messages.PREVALENCE_BY_CONDITION_GRAPH.TOOLTIP}
            />
          </Grid>
        </Grid>
        <Grid style={{ display: 'flex' }}>
          <DiseasePrevalenceTable
            isChronicPrevalence
            data={chronicPrevalenceTableData?.sortedPrevalence}
            handleDiseaseCategory={handleDiseaseCategory}
            highestPrevalenceRate={
              chronicPrevalenceTableData?.highestPrevalenceRate
            }
            handleEpisodeCategory={function () {}}
          />
        </Grid>
      </Grid>
    );
  };

  const [loader] = useState<any>(<Loader />);
  const pathStore = useStores().pathStore;
  const getPath = () => {
    if (pathStore.path.length !== 0) {
      return pathStore.path;
    } else {
      return [];
    }
  };

  return (
    <>
      {isPlanDatesLoading || loading || isInitializing ? (
        loader
      ) : (
        <Grid container className={classes.root}>
          {getPath().length !== 0 && (
            <Grid style={{ marginBottom: '20px' }}>
              <Breadcrumbs itemList={getPath()} />
            </Grid>
          )}

          <Grid
            style={{
              marginBottom: '18px',
              width: '100%',
              position: 'relative',
            }}
          >
            <PageTitleAndSubTitle
              title={messages.TITLE}
              subTitle={getChronicSubTitle()}
              subTitleStyles={{
                width: '100%',
              }}
              showAskAi={true}
              askAiAction={handleAskAi}
              showDownloadButton={true}
              downloadButtonAction={() => {
                downloadPdf(
                  'chronicPrevalenceReport',
                  {
                    employer:
                      employerGroupStore?.employerGroupResponseData
                        ?.employer_group_name,
                    member_unique_id_list: [],
                    claim_id_list: [],
                    maxClaimDate:
                      employerGroupStore?.employerGroupResponseData
                        ?.max_claim_date,
                    ...payload,
                  },
                  handleOpenSnackBar,
                  'open',
                );
                handleOpenSnackBar(
                  snackBarMessages.SNACKBAR_MESSAGES.OPEN_REPORT,
                  'info',
                  6000,
                );
              }}
              showQueryDataButton={checkUserAccess(
                UserAccessConstants.COHORT_DETAILS,
                loginPageStore.userResponse.role.name,
                loginPageStore.userFeatures,
              )}
              queryButtonText={
                cohortDetailsStore.cohortDetailsList?.length > 0 &&
                cohortDetailsStore.cohortDetailsList[0].category !== ''
                  ? 'Edit Cohort'
                  : 'Add Cohort'
              }
              queryDataAction={() => {
                if (
                  checkUserAccess(
                    UserAccessConstants.COHORT_DETAILS,
                    loginPageStore.userResponse.role.name,
                    loginPageStore.userFeatures,
                  )
                ) {
                  openModal();
                }
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '-1px',
                left: '171px',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Typography
                fontSize="17px"
                lineHeight="21px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                fontType="semiBold"
                margin="0px 8px"
              >
                for
              </Typography>
              <DateFilter />
            </div>
            <TabSelection
              selectedTabIndex={selectedTabIndex}
              setSelectedTabIndex={handleSelectedIndex}
              recommendationFetched={recommendationFetched}
            />
          </Grid>
          <Grid>
            {selectedTabIndex === 0 && <SummaryCards />}
            {selectedTabIndex === 1 && (
              <RecommendationsTab
                recommendationLoading={recommendationLoading}
                isInternalPage={true}
              />
            )}
          </Grid>
        </Grid>
      )}
      <AiSummaryCard
        open={activeAskAi}
        onClose={() => {
          setActiveAskAi(false);
          setAiSummaryData([]);
        }}
        aiSummaryData={aiSummaryData || []}
        loading={aiLoading}
      />
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
      <ReportsModal
        open={openReportsModal}
        setOpen={setOpenReportsModal}
        startDate={
          aggregationParameter === 'paid' ? paidStartDate : incurredStartDate
        }
        endDate={
          aggregationParameter === 'paid' ? paidEndDate : incurredEndDate
        }
      />
      <CohortDetailsModal
        open={open}
        handleClose={closeModal}
        totalResults={totalResults}
        isEditMode={isEditMode}
        existingMemberUniqueIds={memberIds}
      />
      <FeatureAccessModal
        open={openFeatureAccessModal}
        handleClose={() => {
          setOpenFeatureAccessModal(false);
        }}
        access={checkUserAccess(
          UserAccessConstants.COHORT_DETAILS,
          loginPageStore.userResponse.role.name,
          loginPageStore.userFeatures,
        )}
      ></FeatureAccessModal>
    </>
  );
});

export default ChronicPrevalencePage;
